import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import '../styles/wennzeBusiness.css';
import { UserContext } from '../contexts/UserContext';

const STEP_LABELS = ['Choisir', 'Configurer', 'Paiement'];

const WennzeBusiness = () => {
  const navigate = useNavigate();
  const { user, redirectToLogin } = useContext(UserContext);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [audience, setAudience] = useState(null);

  // Selected campaign types
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Province & cities data
  const [provinces, setProvinces] = useState([]);

  // Per-campaign config
  const [smsConfig, setSmsConfig] = useState({
    targetType: 'all', provinceId: '', cityIds: [], cities: [],
    recipientCount: '', messageText: '', filteredAudience: null, loadingAudience: false,
  });

  const [pushConfig, setPushConfig] = useState({
    targetType: 'all', provinceId: '', cityIds: [], cities: [],
    recipientCount: '', messageText: '', filteredAudience: null, loadingAudience: false,
  });

  const [posterConfig, setPosterConfig] = useState({
    durationDays: 14, posterFile: null, posterPreview: null,
    posterDims: null, posterValid: null,
  });

  // Order result
  const [orderResult, setOrderResult] = useState(null);

  // Fetch pricing
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${config.backendUrl}/campaigns/business-pricing/`);
        if (res.ok) {
          const data = await res.json();
          setPricing(data.pricing);
          setAudience(data.audience);
        }
      } catch (err) {
        console.error('Failed to load pricing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch provinces
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await fetch(`${config.backendUrl}/users/provinces/`);
        if (res.ok) setProvinces(await res.json());
      } catch (err) {
        console.error('Failed to load provinces:', err);
      }
    };
    fetchProvinces();
  }, []);

  // Fetch cities when province changes
  const fetchCitiesForConfig = useCallback(async (provinceId, type) => {
    if (!provinceId) return;
    try {
      const res = await fetch(`${config.backendUrl}/users/provinces/${provinceId}/cities/`);
      if (res.ok) {
        const data = await res.json();
        const setter = type === 'sms' ? setSmsConfig : setPushConfig;
        setter(prev => ({ ...prev, cities: data, cityIds: [] }));
      }
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  }, []);

  // Fetch filtered audience based on location selection
  const fetchFilteredAudience = useCallback(async (provinceId, cityIds, type) => {
    const setter = type === 'sms' ? setSmsConfig : setPushConfig;
    setter(prev => ({ ...prev, loadingAudience: true }));

    try {
      const params = new URLSearchParams();
      if (provinceId) params.append('province_id', provinceId);
      if (cityIds && cityIds.length > 0) {
        cityIds.forEach(id => params.append('city_ids', id));
      }

      const res = await fetch(`${config.backendUrl}/campaigns/business-pricing/?${params}`);
      if (res.ok) {
        const data = await res.json();
        const targetAudience = type === 'sms' ? data.audience.sms_reachable : data.audience.push_devices;
        setter(prev => ({
          ...prev,
          filteredAudience: data.audience,
          recipientCount: targetAudience.toString(),
          loadingAudience: false,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch filtered audience:', err);
      setter(prev => ({ ...prev, loadingAudience: false }));
    }
  }, []);

  const toggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const formatNumber = (num) => num ? num.toLocaleString('fr-FR') : '0';

  // Cost calculations
  const smsCost = () => {
    if (!pricing || !smsConfig.recipientCount) return 0;
    return parseInt(smsConfig.recipientCount) * parseFloat(pricing.sms.price_per_unit);
  };

  const pushCost = () => {
    if (!pricing || !pushConfig.recipientCount) return 0;
    return parseInt(pushConfig.recipientCount) * parseFloat(pricing.push.price_per_unit);
  };

  const posterCost = () => {
    if (!pricing) return 0;
    const periods = Math.max(1, Math.floor(posterConfig.durationDays / (pricing.poster.period_days || 14)));
    return periods * parseFloat(pricing.poster.price_per_period);
  };

  const grandTotal = () => {
    let total = 0;
    if (selectedTypes.includes('sms')) total += smsCost();
    if (selectedTypes.includes('push')) total += pushCost();
    if (selectedTypes.includes('poster')) total += posterCost();
    return total;
  };

  // Poster handler
  const handlePosterSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPosterConfig(prev => ({ ...prev, posterValid: { ok: false, msg: 'Le fichier doit être une image' }, posterPreview: null, posterFile: null, posterDims: null }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPosterConfig(prev => ({ ...prev, posterValid: { ok: false, msg: "L'image ne doit pas dépasser 5 Mo" }, posterPreview: null, posterFile: null, posterDims: null }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const w = img.width, h = img.height;
        let valid = { ok: true, msg: `Image valide: ${w}×${h}px` };
        if (pricing) {
          const req = pricing.poster.image_requirements;
          if (w < req.min_width || h < req.min_height)
            valid = { ok: false, msg: `Trop petit: ${w}×${h}px. Min: ${req.min_width}×${req.min_height}px` };
          else if (w > req.max_width || h > req.max_height)
            valid = { ok: false, msg: `Trop grand: ${w}×${h}px. Max: ${req.max_width}×${req.max_height}px` };
        }
        setPosterConfig(prev => ({ ...prev, posterFile: file, posterPreview: ev.target.result, posterDims: { width: w, height: h }, posterValid: valid }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }, [pricing]);

  const removePoster = () => {
    setPosterConfig(prev => ({ ...prev, posterFile: null, posterPreview: null, posterDims: null, posterValid: null }));
  };

  // Can proceed?
  const canProceed = () => {
    if (step === 0) return selectedTypes.length > 0;
    if (step === 1) {
      for (const type of selectedTypes) {
        if (type === 'sms' && (!smsConfig.messageText.trim() || !smsConfig.recipientCount || parseInt(smsConfig.recipientCount) <= 0)) return false;
        if (type === 'push' && (!pushConfig.messageText.trim() || !pushConfig.recipientCount || parseInt(pushConfig.recipientCount) <= 0)) return false;
        if (type === 'poster' && (!posterConfig.posterFile || (posterConfig.posterValid && !posterConfig.posterValid.ok) || posterConfig.durationDays <= 0)) return false;
      }
      return true;
    }
    return true;
  };

  // Submit order
  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const items = [];
      if (selectedTypes.includes('sms'))
        items.push({ campaign_type: 'sms', target_type: smsConfig.targetType, target_province: smsConfig.provinceId || null, target_city_ids: smsConfig.cityIds, message_text: smsConfig.messageText, recipient_count: parseInt(smsConfig.recipientCount) });
      if (selectedTypes.includes('push'))
        items.push({ campaign_type: 'push', target_type: pushConfig.targetType, target_province: pushConfig.provinceId || null, target_city_ids: pushConfig.cityIds, message_text: pushConfig.messageText, recipient_count: parseInt(pushConfig.recipientCount) });
      if (selectedTypes.includes('poster'))
        items.push({ campaign_type: 'poster', target_type: 'all', duration_days: parseInt(posterConfig.durationDays), recipient_count: 0, message_text: '' });

      const token = localStorage.getItem('token');
      const res = await fetch(`${config.backendUrl}/campaigns/business-orders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ items }),
      });

      if (res.ok) {
        const order = await res.json();
        // Upload poster if applicable
        if (selectedTypes.includes('poster') && posterConfig.posterFile) {
          const posterItem = order.items.find(i => i.campaign_type === 'poster');
          if (posterItem) {
            const formData = new FormData();
            formData.append('poster_image', posterConfig.posterFile);
            await fetch(`${config.backendUrl}/campaigns/business-orders/upload-poster/${posterItem.id}/`, {
              method: 'POST', headers: { 'Authorization': `Token ${token}` }, body: formData,
            });
          }
        }
        setOrderResult(order);
        setStep(3);
      } else {
        const err = await res.json();
        alert(err.detail || err.error || JSON.stringify(err));
      }
    } catch (err) {
      console.error('Order error:', err);
      alert('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  // ========== RENDER ==========

  if (loading) {
    return (
      <div className="wbiz-page">
        <div className="wbiz-loading"><div className="wbiz-spinner" /><span>Chargement...</span></div>
      </div>
    );
  }

  // Success page
  if (step === 3 && orderResult) {
    return (
      <div className="wbiz-page">
        <div className="wbiz-hero wbiz-hero-sm"><h1>Wennze Business</h1></div>
        <div className="wbiz-success-card">
          <div className="wbiz-success-icon">✔</div>
          <h2>Commande envoyée !</h2>
          <p className="wbiz-success-ref">Référence: <strong>{orderResult.reference}</strong></p>
          <p>Montant total: <strong>${orderResult.total_amount} USD</strong></p>
          <p className="wbiz-success-note">
            Votre commande est en attente d'approbation par notre équipe. Vous serez notifié une fois approuvée.
          </p>
          <button className="wbiz-btn wbiz-btn-primary" onClick={() => {
            setStep(0); setSelectedTypes([]); setOrderResult(null);
            setSmsConfig({ targetType: 'all', provinceId: '', cityIds: [], cities: [], recipientCount: '', messageText: '' });
            setPushConfig({ targetType: 'all', provinceId: '', cityIds: [], cities: [], recipientCount: '', messageText: '' });
            setPosterConfig({ durationDays: 14, posterFile: null, posterPreview: null, posterDims: null, posterValid: null });
          }}>
            Nouvelle commande
          </button>
        </div>
      </div>
    );
  }

  // City selector component
  const renderCitySelector = (cfg, setCfg, configType) => {
    const handleCityToggle = (cityId) => {
      const newCityIds = cfg.cityIds.includes(cityId)
        ? cfg.cityIds.filter(id => id !== cityId)
        : [...cfg.cityIds, cityId];
      
      setCfg(prev => ({ ...prev, cityIds: newCityIds }));
      
      // Fetch updated audience when cities change
      if (newCityIds.length > 0) {
        fetchFilteredAudience(cfg.provinceId, newCityIds, configType);
      } else if (cfg.provinceId) {
        // If no cities selected but province is, fetch province-level audience
        fetchFilteredAudience(cfg.provinceId, [], configType);
      }
    };

    const handleTargetTypeChange = (type) => {
      if (type === 'all') {
        setCfg(prev => ({
          ...prev,
          targetType: 'all',
          provinceId: '',
          cityIds: [],
          cities: [],
          filteredAudience: null,
          recipientCount: '',
        }));
      } else {
        setCfg(prev => ({ ...prev, targetType: 'cities' }));
      }
    };

    return (
      <>
        <div className="wbiz-field-group">
          <label>Ciblage</label>
          <div className="wbiz-target-btns">
            <button className={`wbiz-chip ${cfg.targetType === 'all' ? 'selected' : ''}`}
              onClick={() => handleTargetTypeChange('all')}>
              Toutes les villes
            </button>
            <button className={`wbiz-chip ${cfg.targetType === 'cities' ? 'selected' : ''}`}
              onClick={() => handleTargetTypeChange('cities')}>
              Villes spécifiques
            </button>
          </div>
        </div>

        {cfg.targetType === 'cities' && (
          <div className="wbiz-field-group">
            <label>Province</label>
            <select className="wbiz-select" value={cfg.provinceId}
              onChange={(e) => {
                const val = e.target.value;
                setCfg(prev => ({ ...prev, provinceId: val, cityIds: [], filteredAudience: null }));
                fetchCitiesForConfig(val, configType);
                if (val) {
                  fetchFilteredAudience(val, [], configType);
                }
              }}>
              <option value="">-- Choisir une province --</option>
              {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {cfg.cities.length > 0 && (
              <>
                <label className="wbiz-label-sm">Villes {cfg.cityIds.length > 0 && `(${cfg.cityIds.length} sélectionnée${cfg.cityIds.length > 1 ? 's' : ''})`}</label>
                <div className="wbiz-cities-wrap">
                  {cfg.cities.map(city => (
                    <button key={city.id}
                      className={`wbiz-chip sm ${cfg.cityIds.includes(city.id) ? 'selected' : ''}`}
                      onClick={() => handleCityToggle(city.id)}>
                      {city.name}
                    </button>
                  ))}
                </div>
                {cfg.loadingAudience && (
                  <div className="wbiz-audience-info loading">
                    <i className="fas fa-spinner fa-spin"></i> Calcul de l'audience...
                  </div>
                )}
                {!cfg.loadingAudience && cfg.filteredAudience && cfg.cityIds.length > 0 && (
                  <div className="wbiz-audience-info">
                    <i className="fas fa-users"></i>
                    <span>
                      {configType === 'sms'
                        ? `${formatNumber(cfg.filteredAudience.sms_reachable)} utilisateurs joignables par SMS`
                        : `${formatNumber(cfg.filteredAudience.push_devices)} appareils pour notifications push`}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="wbiz-page">
      {/* Hero */}
      <div className="wbiz-hero wbiz-hero-sm">
        <h1>Wennze Business</h1>
        <p>Boostez votre visibilité en RDC</p>
      </div>

      {/* Stepper */}
      <div className="wbiz-stepper">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className={`wbiz-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <div className="wbiz-step-num">{i < step ? '✔' : i + 1}</div>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Audience bar on step 0 */}
      {audience && step === 0 && (
        <div className="wbiz-stats-bar">
          <div className="wbiz-stat">
            <div className="wbiz-stat-value">{formatNumber(audience.total_users)}</div>
            <div className="wbiz-stat-label">Utilisateurs</div>
          </div>
          <div className="wbiz-stat">
            <div className="wbiz-stat-value">{formatNumber(audience.sms_reachable)}</div>
            <div className="wbiz-stat-label">Joignables SMS</div>
          </div>
          <div className="wbiz-stat">
            <div className="wbiz-stat-value">{formatNumber(audience.push_devices)}</div>
            <div className="wbiz-stat-label">Appareils Push</div>
          </div>
        </div>
      )}

      <div className="wbiz-container">

        {/* ===== STEP 0: SELECT TYPES ===== */}
        {step === 0 && (
          <div className="wbiz-step-content">
            <h2 className="wbiz-section-title">Sélectionnez vos campagnes</h2>
            <p className="wbiz-section-desc">Combinez plusieurs types pour maximiser votre impact</p>
            <div className="wbiz-type-grid">
              {[
                { id: 'sms', icon: 'fas fa-sms', title: 'SMS', desc: 'Message direct sur le téléphone', price: pricing?.sms?.price_per_unit, unit: 'USD / SMS', reach: audience?.sms_reachable, reachLabel: 'utilisateurs joignables' },
                { id: 'push', icon: 'fas fa-bell', title: 'Push Notification', desc: 'Notification instantanée sur l\'app', price: pricing?.push?.price_per_unit, unit: 'USD / notification', reach: audience?.push_devices, reachLabel: 'appareils enregistrés' },
                { id: 'poster', icon: 'fas fa-image', title: 'Affiche Publicitaire', desc: 'Publicité affichée dans l\'app mobile', price: pricing?.poster?.price_per_period, unit: `USD / ${pricing?.poster?.period_days || 14} jours`, reach: audience?.total_users, reachLabel: 'utilisateurs' },
              ].map(t => (
                <div key={t.id} className={`wbiz-type-card ${selectedTypes.includes(t.id) ? 'selected' : ''}`} onClick={() => toggleType(t.id)}>
                  <div className="wbiz-type-check">{selectedTypes.includes(t.id) ? '✔' : ''}</div>
                  <div className={`wbiz-type-icon ${t.id}`}><i className={t.icon} /></div>
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                  {t.price && <div className="wbiz-type-price">${t.price} <small>{t.unit}</small></div>}
                  {t.reach != null && <div className="wbiz-type-reach">{formatNumber(t.reach)} {t.reachLabel}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== STEP 1: CONFIGURE ===== */}
        {step === 1 && (
          <div className="wbiz-step-content">
            <h2 className="wbiz-section-title">Configurez vos campagnes</h2>

            {/* SMS */}
            {selectedTypes.includes('sms') && (
              <div className="wbiz-config-card">
                <div className="wbiz-config-header">
                  <div className="wbiz-type-icon sms sm"><i className="fas fa-sms" /></div>
                  <h3>Campagne SMS</h3>
                </div>
                {renderCitySelector(smsConfig, setSmsConfig, 'sms')}
                <div className="wbiz-field-group">
                  <label>Nombre de destinataires</label>
                  <div className="wbiz-input-row">
                    <input type="number" className="wbiz-input" placeholder="Ex: 1000" value={smsConfig.recipientCount}
                      onChange={(e) => setSmsConfig(prev => ({ ...prev, recipientCount: e.target.value }))} 
                      min="1" 
                      max={smsConfig.filteredAudience?.sms_reachable || audience?.sms_reachable || 999999}
                      disabled={smsConfig.targetType === 'cities' && smsConfig.cityIds.length === 0} />
                    {(smsConfig.filteredAudience || audience) && (
                      <button className="wbiz-use-all" onClick={() => {
                        const maxCount = smsConfig.filteredAudience?.sms_reachable || audience.sms_reachable;
                        setSmsConfig(prev => ({ ...prev, recipientCount: maxCount.toString() }));
                      }}>
                        Max: {formatNumber(smsConfig.filteredAudience?.sms_reachable || audience.sms_reachable)}
                      </button>
                    )}
                  </div>
                  {smsConfig.targetType === 'cities' && smsConfig.cityIds.length === 0 && (
                    <div className="wbiz-hint">Sélectionnez au moins une ville pour continuer</div>
                  )}
                </div>
                <div className="wbiz-field-group">
                  <label>Message SMS</label>
                  <textarea className="wbiz-textarea" rows={4} maxLength={160} placeholder="Votre message SMS (max 160 caractères)..."
                    value={smsConfig.messageText} onChange={(e) => setSmsConfig(prev => ({ ...prev, messageText: e.target.value }))} />
                  <div className="wbiz-char-count">
                    <span>{smsConfig.messageText.length}/160</span>
                    {smsConfig.messageText.length > 0 && (
                      <span className={smsConfig.messageText.length <= 160 ? 'valid' : 'invalid'}>
                        {smsConfig.messageText.length <= 160 ? 'Valide' : 'Trop long'}
                      </span>
                    )}
                  </div>
                </div>
                {pricing && smsConfig.recipientCount && (
                  <div className="wbiz-cost-line">
                    <span>{formatNumber(parseInt(smsConfig.recipientCount))} × ${pricing.sms.price_per_unit}</span>
                    <span className="wbiz-cost-val">${smsCost().toFixed(2)} USD</span>
                  </div>
                )}
              </div>
            )}

            {/* Push */}
            {selectedTypes.includes('push') && (
              <div className="wbiz-config-card">
                <div className="wbiz-config-header">
                  <div className="wbiz-type-icon push sm"><i className="fas fa-bell" /></div>
                  <h3>Notification Push</h3>
                </div>
                {renderCitySelector(pushConfig, setPushConfig, 'push')}
                <div className="wbiz-field-group">
                  <label>Nombre de destinataires</label>
                  <div className="wbiz-input-row">
                    <input type="number" className="wbiz-input" placeholder="Ex: 5000" value={pushConfig.recipientCount}
                      onChange={(e) => setPushConfig(prev => ({ ...prev, recipientCount: e.target.value }))} 
                      min="1" 
                      max={pushConfig.filteredAudience?.push_devices || audience?.push_devices || 999999}
                      disabled={pushConfig.targetType === 'cities' && pushConfig.cityIds.length === 0} />
                    {(pushConfig.filteredAudience || audience) && (
                      <button className="wbiz-use-all" onClick={() => {
                        const maxCount = pushConfig.filteredAudience?.push_devices || audience.push_devices;
                        setPushConfig(prev => ({ ...prev, recipientCount: maxCount.toString() }));
                      }}>
                        Max: {formatNumber(pushConfig.filteredAudience?.push_devices || audience.push_devices)}
                      </button>
                    )}
                  </div>
                  {pushConfig.targetType === 'cities' && pushConfig.cityIds.length === 0 && (
                    <div className="wbiz-hint">Sélectionnez au moins une ville pour continuer</div>
                  )}
                </div>
                <div className="wbiz-field-group">
                  <label>Texte de la notification</label>
                  <textarea className="wbiz-textarea" rows={3} maxLength={256} placeholder="Votre notification push (max 256 caractères)..."
                    value={pushConfig.messageText} onChange={(e) => setPushConfig(prev => ({ ...prev, messageText: e.target.value }))} />
                  <div className="wbiz-char-count">
                    <span>{pushConfig.messageText.length}/256</span>
                    {pushConfig.messageText.length > 0 && (
                      <span className={pushConfig.messageText.length <= 256 ? 'valid' : 'invalid'}>
                        {pushConfig.messageText.length <= 256 ? 'Valide' : 'Trop long'}
                      </span>
                    )}
                  </div>
                </div>
                {pricing && pushConfig.recipientCount && (
                  <div className="wbiz-cost-line">
                    <span>{formatNumber(parseInt(pushConfig.recipientCount))} × ${pricing.push.price_per_unit}</span>
                    <span className="wbiz-cost-val">${pushCost().toFixed(2)} USD</span>
                  </div>
                )}
              </div>
            )}

            {/* Poster */}
            {selectedTypes.includes('poster') && (
              <div className="wbiz-config-card">
                <div className="wbiz-config-header">
                  <div className="wbiz-type-icon poster sm"><i className="fas fa-image" /></div>
                  <h3>Affiche Publicitaire</h3>
                </div>
                <div className="wbiz-field-group">
                  <label>Durée</label>
                  <div className="wbiz-duration-btns">
                    {[14, 28, 42, 56].map(d => (
                      <button key={d} className={`wbiz-chip ${posterConfig.durationDays === d ? 'selected' : ''}`}
                        onClick={() => setPosterConfig(prev => ({ ...prev, durationDays: d }))}>{d} jours</button>
                    ))}
                  </div>
                </div>
                <div className="wbiz-field-group">
                  <label>Votre affiche (Format 2:1)</label>
                  {!posterConfig.posterPreview ? (
                    <label className={`wbiz-dropzone ${posterConfig.posterValid?.ok === false ? 'error' : ''}`}>
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePosterSelect} style={{ display: 'none' }} />
                      <div className="wbiz-dropzone-icon">�</div>
                      <div className="wbiz-dropzone-text">Déposez votre affiche ou cliquez ici</div>
                      {pricing && <div className="wbiz-dropzone-hint">
                        {pricing.poster.image_requirements.min_width}×{pricing.poster.image_requirements.min_height}px min — {pricing.poster.image_requirements.max_width}×{pricing.poster.image_requirements.max_height}px max • Max 5 Mo
                      </div>}
                    </label>
                  ) : (
                    <div className="wbiz-poster-preview">
                      <img src={posterConfig.posterPreview} alt="Aperçu" />
                      <button className="wbiz-poster-preview-remove" onClick={removePoster}>✕</button>
                    </div>
                  )}
                  {posterConfig.posterValid && (
                    <div className={`wbiz-validation ${posterConfig.posterValid.ok ? 'success' : 'error'}`}>
                      {posterConfig.posterValid.ok ? '✔' : '✕'} {posterConfig.posterValid.msg}
                    </div>
                  )}
                </div>
                {pricing && (
                  <div className="wbiz-cost-line">
                    <span>{Math.max(1, Math.floor(posterConfig.durationDays / (pricing.poster.period_days || 14)))} période(s) × ${pricing.poster.price_per_period}</span>
                    <span className="wbiz-cost-val">${posterCost().toFixed(2)} USD</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== STEP 2: CHECKOUT ===== */}
        {step === 2 && (
          <div className="wbiz-step-content">
            <h2 className="wbiz-section-title">Récapitulatif & Paiement</h2>
            <div className="wbiz-checkout-card">
              {selectedTypes.includes('sms') && (
                <div className="wbiz-checkout-item">
                  <div className="wbiz-checkout-item-header">
                    <span className="wbiz-checkout-badge sms">SMS</span>
                    <span className="wbiz-checkout-price">${smsCost().toFixed(2)} USD</span>
                  </div>
                  <div className="wbiz-checkout-details">
                    <div><strong>Destinataires:</strong> {formatNumber(parseInt(smsConfig.recipientCount))}</div>
                    <div><strong>Ciblage:</strong> {smsConfig.targetType === 'all' ? 'Toutes les villes' : 'Villes spécifiques'}</div>
                    <div><strong>Message:</strong> {smsConfig.messageText.substring(0, 80)}{smsConfig.messageText.length > 80 ? '...' : ''}</div>
                  </div>
                </div>
              )}
              {selectedTypes.includes('push') && (
                <div className="wbiz-checkout-item">
                  <div className="wbiz-checkout-item-header">
                    <span className="wbiz-checkout-badge push">Push</span>
                    <span className="wbiz-checkout-price">${pushCost().toFixed(2)} USD</span>
                  </div>
                  <div className="wbiz-checkout-details">
                    <div><strong>Destinataires:</strong> {formatNumber(parseInt(pushConfig.recipientCount))}</div>
                    <div><strong>Ciblage:</strong> {pushConfig.targetType === 'all' ? 'Toutes les villes' : 'Villes spécifiques'}</div>
                    <div><strong>Message:</strong> {pushConfig.messageText.substring(0, 80)}{pushConfig.messageText.length > 80 ? '...' : ''}</div>
                  </div>
                </div>
              )}
              {selectedTypes.includes('poster') && (
                <div className="wbiz-checkout-item">
                  <div className="wbiz-checkout-item-header">
                    <span className="wbiz-checkout-badge poster">Poster</span>
                    <span className="wbiz-checkout-price">${posterCost().toFixed(2)} USD</span>
                  </div>
                  <div className="wbiz-checkout-details">
                    <div><strong>Durée:</strong> {posterConfig.durationDays} jours</div>
                    {posterConfig.posterPreview && (
                      <div className="wbiz-checkout-poster-thumb"><img src={posterConfig.posterPreview} alt="Poster" /></div>
                    )}
                  </div>
                </div>
              )}

              <div className="wbiz-checkout-total">
                <span>Total à payer</span>
                <span className="wbiz-grand-total">${grandTotal().toFixed(2)} USD</span>
              </div>

              <div className="wbiz-checkout-notice">
                <i className="fas fa-info-circle" />
                <span>Votre commande sera examinée par notre équipe avant activation. Vous serez notifié par email.</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="wbiz-nav-bar">
          {step > 0 && (
            <button className="wbiz-btn wbiz-btn-outline" onClick={() => setStep(step - 1)}>← Retour</button>
          )}
          <div className="wbiz-nav-spacer" />
          {step < 2 && (
            <button className="wbiz-btn wbiz-btn-primary" disabled={!canProceed()}
              onClick={() => {
                setStep(step + 1);
              }}>
              Continuer →
            </button>
          )}
          {step === 2 && (
            <button className="wbiz-btn wbiz-btn-pay" disabled={submitting} onClick={submitOrder}>
              {submitting ? 'Envoi en cours...' : `Payer $${grandTotal().toFixed(2)} USD`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WennzeBusiness;
